import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import CreditsPricingSheet from "./pricing/CreditsPricingSheet";
import {
  copyPricingPorMotivo,
  type MotivoPricing,
} from "./pricing/pricingContexts";
import { palabraDeModeloActual } from '../lib/nombreDeModelo';
import { useUltimoSaldo } from '../hooks/useSaldo';
import { asegurarSaldoLeido, type Saldo } from '../lib/saldo';
import ElegirPlanDeMarca from './facturacionPorMarca/ElegirPlanDeMarca';
import { pedidoDePlanDeMarca } from './facturacionPorMarca/planDeMarca';

/**
 * Bloqueos de suscripción. Hoy es un **router** con dos destinos muy distintos.
 * Ver ADR-2026-07-30-copy-catalogo-segun-contexto-abrio.
 *
 * - **`free-trial-ended` / `plan-limit-reached`** — el usuario se quedó sin
 *   créditos al intentar generar. Antes cada uno tenía su propia pantalla: la de
 *   "¡QUÉDATE CON NOSOTROS!" con las 30 imágenes gratis / 30% OFF y su contador de
 *   57 minutos, y la del carrusel polaroid con el límite del ciclo. Las dos se
 *   quitaron: ahora se muestra **directamente el catálogo de planes**
 *   (`CreditsPricingSheet`), con el copy de "te quedaste sin créditos". Los dos
 *   tipos quedan en el union porque los 8 call-sites los siguen distinguiendo
 *   (`plan === 'gratis' ? … : …`) y hoy llevan al mismo lugar.
 * - **`multi-brand-required`** — NO es falta de créditos sino una función que el
 *   plan gratis no incluye. Sigue con su modal chico de siempre.
 * - **`tope-de-marcas`** — un plan pago que ya usó todas las marcas que incluye
 *   (ADR-2026-10-27-planes-de-equipo-en-el-front). Mismo modal chico, con el número del plan
 *   y "Ver planes".
 *
 * La firma no cambió a propósito: los 12 lugares que lo montan siguen pasando
 * `isOpen` / `onClose` / `type` y no hubo que tocar ninguno. Todas las ramas de
 * cobro que vivían acá (plan a medida → WhatsApp, falla → `/subscription`, Stripe
 * con suscripción activa → Billing Portal) se conservan dentro de
 * `CreditsPricingSheet`.
 */
interface SubscriptionLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: "free-trial-ended" | "plan-limit-reached" | "multi-brand-required" | "tope-de-marcas";
  /** Con `tope-de-marcas`: cuántas marcas incluye el plan. */
  tope?: number | null;
  /**
   * Estado del saldo, para elegir el copy del catálogo — ADR-2026-07-31-copy-catalogo-sigue-saldo-real.
   *
   * Por defecto `'sin'`, que es lo que significan los 11 call-sites históricos: se
   * abren **al chocar** contra la falta de créditos. Lo pasa quien puede abrirlo
   * con saldo todavía disponible, como el CTA de la barra superior (ADR-2026-07-31-fuera-cartel-30-imagenes-gratis), donde
   * "Te quedaste sin créditos" sería falso.
   *
   * No se deduce del `type`: `free-trial-ended` y `plan-limit-reached` distinguen
   * **por qué** se acabaron los créditos, no **si** quedan.
   */
  motivo?: MotivoPricing;
}

const SubscriptionLimitModal: React.FC<SubscriptionLimitModalProps> = ({
  isOpen,
  onClose,
  type,
  motivo = "sin",
  tope = null,
}) => {
  // Facturación por marca (ADR-2026-10-28-elegir-plan-de-marca): el "sin créditos" es de la
  // MARCA activa, así que en vez del catálogo de la empresa va su plan (dueño: elegir o
  // subir de nivel; miembro: "Pedile al admin"), aunque no haya marca resuelta (el modal
  // muestra el error). Sale del último saldo leído: sin bandera `pedido` es null y todo sigue
  // igual que antes.
  const ultimoSaldo = useUltimoSaldo();
  // Sin saldo leído todavía no se adivina: se lee y se decide con lo que devuelve ESA lectura
  // (`asegurarSaldoLeido`, con tope de 3 s; si tarda, falla o no hay sesión → `null` = el
  // catálogo de siempre). Mientras tanto no se dibuja nada. ADR-2026-10-28-elegir-plan-de-marca-3.
  const deCreditos = type !== "multi-brand-required";
  const [leido, setLeido] = useState<{ saldo: Saldo | null } | null>(null);
  const leyendo = isOpen && deCreditos && !ultimoSaldo && !leido;
  useEffect(() => {
    if (!leyendo) return;
    let vivo = true;
    void asegurarSaldoLeido().then((saldo) => {
      if (vivo) setLeido({ saldo });
    });
    return () => {
      vivo = false;
    };
  }, [leyendo]);
  // Si no se pudo saber (tope, falla), la próxima apertura vuelve a intentar. Una lectura buena
  // se conserva: así el modal de la marca sale con su transición al cerrar.
  useEffect(() => {
    if (!isOpen && leido && !leido.saldo) setLeido(null);
  }, [isOpen, leido]);
  const pedido = pedidoDePlanDeMarca(ultimoSaldo ?? leido?.saldo ?? null);
  if (leyendo) return null;
  if (pedido && deCreditos) {
    return (
      <ElegirPlanDeMarca
        abierto={isOpen}
        brandId={pedido.brandId}
        modo={pedido.modo}
        // Se abrió al chocar contra el saldo: aunque quede algo, para lo pedido no alcanza.
        sinCreditos={motivo === "sin"}
        onListo={onClose}
      />
    );
  }

  // Los callers lo montan siempre y el gate vive acá adentro (no lo desmontan al
  // cerrarlo), así que este `return null` es el que apaga todo.
  if (!isOpen) return null;

  if (type === "multi-brand-required") {
    return <MultiBrandRequiredModal onClose={onClose} />;
  }
  if (type === "tope-de-marcas") {
    return <TopeDeMarcasModal onClose={onClose} tope={tope} />;
  }

  // `free-trial-ended` = prueba gratis: el copy no puede hablar de "mejorar tu plan"
  // ni de esperar a fin de mes, porque no tiene plan ni renovación
  // (ADR-2026-08-03-copy-prueba-gratis-suscribirse).
  const pruebaGratis = type === "free-trial-ended";
  const { copy, skipLabel, nota } = copyPricingPorMotivo(motivo, { pruebaGratis });
  // Prueba gratis sin créditos: se cae el "Seguir sin créditos". Sin créditos no se
  // puede hacer nada, así que ese link ofrece una salida que no existe y confunde
  // (ADR-2026-08-03-sin-seguir-sin-creditos-en-prueba-gratis). El catálogo se sigue
  // cerrando con la X de arriba y, en mobile, arrastrándolo hacia abajo.
  const sinSalida = pruebaGratis && motivo === "sin";
  return (
    <CreditsPricingSheet
      onClose={onClose}
      copy={copy}
      skipLabel={sinSalida ? null : skipLabel}
      nota={nota}
    />
  );
};

/**
 * Crear marcas adicionales requiere un plan pago. Es gating **por plan**, no por
 * créditos: el copy y el CTA se conservan tal cual estaban.
 */
function MultiBrandRequiredModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();

  // Portaleado a document.body: el lightbox de las galerías se renderiza con
  // createPortal en el contexto raíz; si este modal queda inline dentro del shell
  // `transform-gpu`, su z-index queda atrapado y aparece por debajo del lightbox.
  return createPortal(
    <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center">
      <div className="bg-surface-card rounded-lg shadow-md w-full max-w-xl mx-4">
        <div className="p-5">
          <h3 className="text-[17px] sm:text-[19px] font-semibold text-brand-black/90 mb-2 text-center sm:text-left">
            Crear marcas adicionales requiere un plan pago
          </h3>
          <p className="text-[15px] text-brand-black/90 mb-5 text-center sm:text-left">
            Con un plan pago vas a poder crear y gestionar múltiples marcas dentro
            de tu cuenta, cada una con su propia identidad visual, productos y{' '}
            {palabraDeModeloActual().plural}.
          </p>
          <p className="text-[15px] text-neutral-600 mb-6 text-center sm:text-left">
            Pasate a un plan pago y desbloqueá esta función al instante.
          </p>
          <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-center sm:justify-end">
            <button
              onClick={onClose}
              className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium rounded-md shadow-sm hover:bg-neutral-100 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                navigate("/subscription");
                onClose();
              }}
              className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
            >
              Ir a planes
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * A dónde lleva "Ver planes": con 1 marca (Básico, Pro) el siguiente paso es Ultra, que está en
 * el catálogo; con 3 o más (Ultra, Equipos) sólo quedan los planes de equipo (pedido de Nacho).
 */
export function destinoDelTopeDeMarcas(tope: number | null): string {
  return tope !== null && tope >= 3 ? "/subscription?equipos=1" : "/subscription";
}

/** El título del modal del tope: "Tu plan incluye 1 marca" / "…hasta 3 marcas". */
export function tituloTopeDeMarcas(tope: number | null): string {
  if (!tope) return "Llegaste al máximo de marcas de tu plan";
  return tope === 1 ? "Tu plan incluye 1 marca" : `Tu plan incluye hasta ${tope} marcas`;
}

/**
 * Un plan pago que ya usó todas sus marcas. Mismo modal y mismos botones que
 * `MultiBrandRequiredModal` (no se rediseña nada): cambia el texto, que dice el tope del plan
 * y qué planes traen más.
 */
function TopeDeMarcasModal({ onClose, tope }: { onClose: () => void; tope: number | null }) {
  const navigate = useNavigate();

  return createPortal(
    <div className="fixed inset-0 bg-brand-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center">
      <div role="dialog" aria-modal="true" className="bg-surface-card rounded-lg shadow-md w-full max-w-xl mx-4">
        <div className="p-5">
          <h3 className="text-[17px] sm:text-[19px] font-semibold text-brand-black/90 mb-2 text-center sm:text-left">
            {tituloTopeDeMarcas(tope)}
          </h3>
          <p className="text-[15px] text-brand-black/90 mb-5 text-center sm:text-left">
            Ya las estás usando todas. Para sumar otra marca, pasate a un plan con más: Ultra
            incluye 3, Equipos 10 y Agencias, todas las que necesites.
          </p>
          <div className="flex flex-col sm:flex-row gap-2.5 items-center justify-center sm:justify-end">
            <button
              onClick={onClose}
              className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto border border-neutral-200 bg-surface-card text-brand-black/90 text-[15px] font-medium rounded-md shadow-sm hover:bg-neutral-100 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                navigate(destinoDelTopeDeMarcas(tope));
                onClose();
              }}
              className="inline-flex items-center justify-center text-center h-10 px-4 w-full sm:w-auto bg-brand-primary hover:bg-brand-primary-hover text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
            >
              Ver planes
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default SubscriptionLimitModal;
